import React, { useState, useEffect } from 'react';
import TagInput from '../common/TagInput';
import { docsApi } from '../../api';
import { toast } from 'react-toastify';

export default function DocumentModal({ doc, onClose, onSaved }) {
  const isEdit = !!doc?.id;

  const [title,   setTitle]   = useState(doc?.title   ?? '');
  const [content, setContent] = useState(doc?.content ?? '');
  const [tags,    setTags]    = useState(doc?.tags ? [...doc.tags] : []);
  const [saving,  setSaving]  = useState(false);
  const [errors,  setErrors]  = useState({});

  useEffect(() => {
    // Re-populate if doc changes (e.g. switching edit targets)
    setTitle(doc?.title ?? '');
    setContent(doc?.content ?? '');
    setTags(doc?.tags ? [...doc.tags] : []);
    setErrors({});
  }, [doc]);

  const validate = () => {
    const e = {};
    if (!title.trim())   e.title   = 'Title is required';
    if (!content.trim()) e.content = 'Content is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        const oldTags = new Set(doc.tags ?? []);
        const newTags = new Set(tags);
        const tagsToAdd    = tags.filter(t => !oldTags.has(t));
        const tagsToRemove = [...oldTags].filter(t => !newTags.has(t));

        const payload = {};
        if (title !== doc.title)     payload.title   = title;
        if (content !== doc.content) payload.content = content;
        if (tagsToAdd.length)    payload.tagsToAdd    = tagsToAdd;
        if (tagsToRemove.length) payload.tagsToRemove = tagsToRemove;

        const { data } = await docsApi.update(doc.id, payload);
        toast.success('Document updated');
        onSaved?.(data);
      } else {
        const { data } = await docsApi.create({ title, content, tags });
        toast.success('Document created');
        onSaved?.(data);
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message ?? 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box">
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Document' : 'New Document'}</span>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Title</label>
            <input
              className={`form-input ${errors.title ? 'error' : ''}`}
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Give your document a clear title"
              autoFocus
            />
            {errors.title && <div style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: '0.3rem' }}>{errors.title}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Content</label>
            <textarea
              className={`form-input ${errors.content ? 'error' : ''}`}
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Write your document content here..."
              rows={8}
            />
            {errors.content && <div style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: '0.3rem' }}>{errors.content}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Tags</label>
            <TagInput tags={tags} onChange={setTags} />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
              Press Enter, comma, or space to add a tag
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary-custom" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner" /> Saving…</> : (isEdit ? 'Save Changes' : 'Create Document')}
          </button>
        </div>
      </div>
    </div>
  );
}
